import {
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { FORM_FIELDS } from './constants/form-fields';
import { FORM_PATHS } from './constants/form-paths';
import { ContentTypes } from 'src/app/shared/enums/content-types';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { LanguageService } from 'src/app/content-management/shared/services/language.service';
import { DeliveryTargetsService } from 'src/app/content-list-management/shared/services/cmsapi/delivery-targets.service';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UserSettingsService } from 'src/app/shared/services/user-settings.service';
import { WithDeactivateGuard } from 'src/app/shared/mixins/with-deactivate-guard';
import { WithHeadlineFormSection } from 'src/app/shared/mixins/with-headline-form-section';
import { WithLanguagesFormSection } from 'src/app/shared/mixins/with-languages-form-section';
import { WithNextStateFormSection } from 'src/app/shared/mixins/with-next-states-form-section';
import { WithProcessing } from 'src/app/shared/mixins/with-processing';
import { WithStandfirstFormSection } from 'src/app/shared/mixins/with-standfirst-form-section';
import { WithWebsitesFormSection } from 'src/app/shared/mixins/with-websites-form-section';
import { catchError, filter, finalize, switchMap, tap } from 'rxjs/operators';
import { ContentTypeRouterPaths } from 'src/app/shared/enums/content-type-router-paths';
import { IWebsiteUrl } from 'src/app/core/dtos/interfaces/i-website-url';
import { IAllowableState } from 'src/app/core/dtos/interfaces/i-allowable-state';
import { ContentStatus } from 'src/app/shared/enums/content-status';
import { ISaveContentResponseDto } from 'src/app/core/dtos/interfaces/i-save-content-response-dto';
import { WithPublishTimeFormSection } from 'src/app/shared/mixins/with-publish-time';
import { WithContentListFormSection } from 'src/app/shared/mixins/with-content-lists-form-section';
import { ContentListService } from 'src/app/content-list-management/shared/services/cmsapi/content-list.service';
import { IContentListDto } from 'src/app/core/dtos/interfaces/i-content-list-dto';
import { WithContentListFromWebsite } from 'src/app/shared/mixins/with-content-lists-from-websites';
import { WithRelatedDataFormSection } from 'src/app/shared/mixins/with-related-data-form-section';
import { ManagerService } from 'src/app/shared/services/manager.service';
import { IManager } from 'src/app/core/models/i-manager';
import { IFund } from 'src/app/core/models/i-fund';
import { ISector } from 'src/app/core/models/i-sector';
import { IAssetClass } from 'src/app/core/models/i-asset-class';
import { IEtf } from 'src/app/core/models/i-etf';
import { IInvestmentTrust } from 'src/app/core/models/i-investment-trust';
import { IShare } from 'src/app/core/models/i-share';
import { WithAuthorsFormSection } from 'src/app/shared/mixins/with-authors-form-section';
import { AuthorService } from 'src/app/shared/services/author.service';
import { IAuthor } from 'src/app/core/models/i-author';
import { WithRelatedArticlesFormSection } from 'src/app/shared/mixins/with-related-articles-form-section';
import { IRelatedContent } from 'src/app/core/models/i-related-content';
import { IExternalLink } from 'src/app/core/models/i-external-link';
import { WithExternalLinksFormSection } from 'src/app/shared/mixins/with-external-links';
import { WithTaggingFormSection } from 'src/app/shared/mixins/with-tagging-form-section';
import { ITag, Tag } from 'src/app/core/models/i-tag';
import { ITopic } from 'src/app/core/models/i-topic';
import { ICompany } from 'src/app/core/models/i-company';
import { IPerson } from 'src/app/core/models/i-person';
import { WithContentVisibilityFormSection } from 'src/app/shared/mixins/with-visibility-section';
import { WithColumnistContentFormSection } from 'src/app/shared/mixins/with-columnist-content-form-section';
import { IImgMediaDto } from 'src/app/core/dtos/interfaces/i-img-media-dto';
import { IRepurposeContentResponse } from 'src/app/core/dtos/interfaces/i-repurpose-content-response-dto';
import { ContentStateService } from 'src/app/content/services/content-state.service';
import { IErrorResponse } from 'src/app/core/dtos/interfaces/i-error-response';
import { SharedContentTypeService } from 'src/app/content/services/shared-content-type.service';
import { GalleryContentTypeService } from 'src/app/content/services/gallery-content-type.service';
import { IGalleryContentDto } from 'src/app/core/dtos/interfaces/i-gallery-content-dto';
import { GalleryContentDto } from 'src/app/core/dtos/gallery-content-dto';
import { WithMainVideoImageFormSection } from 'src/app/shared/mixins/with-main-video-image-section';
import { ISlide } from 'src/app/core/dtos/interfaces/i-slide';
import { WithSlidesFormSection } from 'src/app/shared/mixins/with-slides-form-section';
import { MediaService } from 'src/app/shared/services/media.service';
import { USER_ROLES } from 'src/app/core/constants/cms-user-roles';
import { UserRolesService } from 'src/app/shared/services/user-roles.service';
import { SlideListComponent } from 'src/app/shared-form-sections/components/slide-list/slide-list.component';
import { SignalrService } from 'src/app/content-management/shared/services/signalr.service';
import { WithSurtitlesFormSection } from 'src/app/shared/mixins/with-surtitles-form-section';
import { ITypedSelectListItem } from 'src/app/shared-form-sections/interfaces/i-typed-select-list-item';
import { ISurtitle } from 'src/app/core/models/i-surtitle';
import { TopicService } from 'src/app/content-management/shared/services/topic.service';
import {
  handleTagSelected,
  handleTagRemoved,
  handleTagAddedAsSurtitle,
} from 'src/app/shared/utils/tagging-handler';

class GalleryContentFormComponentBase {
  FORM_PATHS = FORM_PATHS;
  CONTENT_TYPES = ContentTypes;
  destroy$ = new Subject<boolean>();
  form = this.formBuilder.group({});
  formReady = new BehaviorSubject<boolean>(false);

  constructor(
    public languageService: LanguageService,
    public deliveryTargetsService: DeliveryTargetsService,
    public formBuilder: FormBuilder,
    public _dialogService: DialogService,
    public contentListService: ContentListService,
    public managerService: ManagerService,
    public authorService: AuthorService,
    public mediaService: MediaService,
    public signalRService: SignalrService,
    public topicService: TopicService
  ) {}
}

const GalleryContentFormComponentMixinBase = WithProcessing(
  WithDeactivateGuard(
    WithHeadlineFormSection(
      WithStandfirstFormSection(
        WithNextStateFormSection(
          WithLanguagesFormSection(
            WithPublishTimeFormSection(
              WithRelatedDataFormSection(
                WithRelatedArticlesFormSection(
                  WithAuthorsFormSection(
                    WithContentVisibilityFormSection(
                      WithContentListFormSection(
                        WithTaggingFormSection(
                          WithSurtitlesFormSection(
                            WithContentListFromWebsite(
                              WithWebsitesFormSection(
                                WithExternalLinksFormSection(
                                  WithColumnistContentFormSection(
                                    WithMainVideoImageFormSection(
                                      WithSlidesFormSection(
                                        GalleryContentFormComponentBase
                                      )
                                    )
                                  )
                                )
                              )
                            )
                          )
                        )
                      )
                    )
                  )
                )
              )
            )
          )
        )
      )
    )
  )
);

@Component({
  selector: 'app-gallery-content-form',
  templateUrl: './gallery-content-form.component.html',
  styleUrls: ['./gallery-content-form.component.scss'],
})
export class GalleryContentFormComponent
  extends GalleryContentFormComponentMixinBase
  implements OnInit, OnDestroy
{
  FORM_FIELDS = FORM_FIELDS;
  FORM_PATHS = FORM_PATHS;
  CONTENT_TYPES = ContentTypes;
  destroy$ = new Subject<boolean>();
  formReady = new BehaviorSubject<boolean>(false);

  isSaving = false;
  isProcessingStateChange = false;
  isEditMode = false;
  isNewGallery = false;

  id: string;
  pageHeader: string;
  galleryContent$!: Observable<IGalleryContentDto>;
  initialGalleryContent!: IGalleryContentDto;
  currentUserName: string = '';

  @ViewChild(SlideListComponent)
  slideListComponent: SlideListComponent;

  form = this.buildForm();

  canRepurpose$: Observable<boolean>;

  private buildForm(): FormGroup {
    return this.formBuilder.group({
      [this.FORM_FIELDS.HEADLINE]: ['', Validators.required],
      [this.FORM_FIELDS.STANDFIRST]: [''],
      [this.FORM_FIELDS.LANGUAGE]: [null],
      [this.FORM_FIELDS.WEBSITES]: this.formBuilder.group(
        {},
        {
          validators: [this._initWebsiteValidation()],
        }
      ),
      [this.FORM_FIELDS.INTERNAL_INFORMATION]: this.formBuilder.group({
        [this.FORM_FIELDS.LIVE_URLS]: this.formBuilder.array([]),
        [this.FORM_FIELDS.CONTENT_IDS]: [],
      }),
      [this.FORM_FIELDS.PUBLISH]: this.formBuilder.group(
        {
          [this.FORM_FIELDS.DATE]: [],
          [this.FORM_FIELDS.TIMEZONE]: [],
          [this.FORM_FIELDS.SHOW_PUBLISH_TIME]: [false],
        },
        {
          validators: [this._initPublishTimeValidation()],
        }
      ),
      [this.FORM_FIELDS.ARTICLE_VERSION_LIST]: this.formBuilder.array([]),
      [this.FORM_FIELDS.NEXT_STATES]: [],
      [this.FORM_FIELDS.SELECTED_AUTHORS]: this.formBuilder.array([]),
      [this.FORM_FIELDS.CONTENT_LISTS]: this.formBuilder.array([]),
      [this.FORM_FIELDS.RELATED_MANAGERS]: this.formBuilder.array([]),
      [this.FORM_FIELDS.RELATED_FUNDS]: this.formBuilder.array([]),
      [this.FORM_FIELDS.RELATED_SECTORS]: this.formBuilder.array([]),
      [this.FORM_FIELDS.RELATED_ASSET_CLASSES]: this.formBuilder.array([]),
      [this.FORM_FIELDS.RELATED_ETFS]: this.formBuilder.array([]),
      [this.FORM_FIELDS.RELATED_INVESTMENT_TRUSTS]: this.formBuilder.array([]),
      [this.FORM_FIELDS.RELATED_SHARES]: this.formBuilder.array([]),
      [this.FORM_FIELDS.RELATED_ARTICLES]: this.formBuilder.array([]),
      [this.FORM_FIELDS.COLUMNIST_CONTENT]: this.formBuilder.control(false),
      [this.FORM_FIELDS.EXTERNAL_LINKS]: this.formBuilder.array([]),
      [this.FORM_FIELDS.SURTITLES]: this.formBuilder.array([]),
      [this.FORM_FIELDS.EDITION_LANGUAGE]: ['en'],
      [this.FORM_FIELDS.TYPED_TAGS]: this.formBuilder.array([]),
      [this.FORM_FIELDS.TAGS]: this.formBuilder.array([]),
      [this.FORM_FIELDS.TOPICS]: this.formBuilder.array([]),
      [this.FORM_FIELDS.TOPICS_WITH_TRANSLATIONS]: this.formBuilder.array([]),
      [this.FORM_FIELDS.COMPANIES]: this.formBuilder.array([]),
      [this.FORM_FIELDS.PEOPLE]: this.formBuilder.array([]),
      [this.FORM_FIELDS.RESTRICTED_TO_REGISTERED_USERS]: [null],
      [this.FORM_FIELDS.NOT_FOR_GOOGLE_NEWS_SITEMAPS]: [null],
      [this.FORM_FIELDS.NOT_FOR_NEWS_FEED]: [null],
      [this.FORM_FIELDS.HIDE_THIS_ARTICLE]: [null],
      [this.FORM_FIELDS.PAYWALL_PREMIUM_CONTENT]: [null],
      [this.FORM_FIELDS.NOT_FOR_COMPETITORS]: [null],
      [this.FORM_FIELDS.MAIN_IMAGE]: this.formBuilder.array<IImgMediaDto[]>([]),
      [this.FORM_FIELDS.DISPLAY_IMAGE_ON_WEBSITE]:
        this.formBuilder.control<boolean>(false),
      [this.FORM_FIELDS.SLIDES]: this.formBuilder.array<FormControl<ISlide>>(
        []
      ),
    });
  }

  readonly getContentFn$ = (id: string) => {
    return this.getGalleryContent$(id);
  };

  readonly createNewVersionFn$ = (contentId, versionId) =>
    this.galleryService.createVersion$(contentId, versionId);

  readonly deleteVersionFn$ = (contentId, versionId) =>
    this.galleryService.deleteVersion$(contentId, versionId);

  @HostListener('window:beforeunload', ['$event'])
  beforeUnload(event: Event): void {
    this.handleBeforeUnload(event);
  }

  constructor(
    public formBuilder: FormBuilder,
    public languageService: LanguageService,
    public deliveryTargetsService: DeliveryTargetsService,
    public galleryService: GalleryContentTypeService,
    public sharedService: SharedContentTypeService,
    public _dialogService: DialogService,
    public authorService: AuthorService,
    public mediaService: MediaService,
    public signalRService: SignalrService,
    public topicService: TopicService,
    public contentListService: ContentListService,
    public managerService: ManagerService,
    public userRolesService: UserRolesService,
    private _activatedRoute: ActivatedRoute,
    private _snackBar: MatSnackBar,
    private _router: Router,
    private _userSettingsService: UserSettingsService,
    private _contentStateService: ContentStateService
  ) {
    super(
      languageService,
      deliveryTargetsService,
      formBuilder,
      _dialogService,
      contentListService,
      managerService,
      authorService,
      mediaService,
      signalRService,
      topicService
    );
  }

  ngOnInit(): void {
    this._routerUrlSub();
    this.canRepurpose$ = this.userRolesService.isUserInRole(
      USER_ROLES.CanRepurposeArticles
    );

    if (this.isNewGallery) {
      this.pageHeader = 'Create Gallery';
      this.initialGalleryContent = new GalleryContentDto({});
      this.initialGalleryContent.dynamicData.allowableNextStates = [];
      this.initialGalleryContent.dynamicData.versions = [];
      this.initialGalleryContent.dynamicData.liveUrls = [];
      this.initialGalleryContent.dynamicData.previewUrls = [];

      this._userSettingsService.getForCurrentUser$().subscribe((x) => {
        this.initialGalleryContent.languageCode = x.defaultLanguageCode;
        this.initialGalleryContent.deliveryTargetCodes =
          x.defaultDeliveryTargetCodes ?? [];
        this.initialGalleryContent.authors = x.defaultArticleAuthors ?? [];
        this._initContentListsForNewContent(x.defaultDeliveryTargetCodes);
        this._initForm();
      });
    } else {
      this.pageHeader = 'Edit Gallery';
      this._routeParamsSub();
    }

    if (!this.isEditMode && !this.isNewGallery) {
      this.form.disable();
    }

    this._userSettingsService.getCurrentUserName$().subscribe((username) => {
      this.currentUserName = username;
    });
  }

  private _routerUrlSub(): void {
    this._activatedRoute.url.subscribe((path) => {
      const segments = path.map((segment) => segment.path);
      this.isEditMode =
        segments.length > 0 &&
        (segments.includes('edit') || segments.includes('new'));

      this.isNewGallery = segments.length > 0 && segments.includes('new');
    });
  }

  private _routeParamsSub(): void {
    this._activatedRoute.paramMap
      .pipe(
        filter((paramMaps) => {
          return !!paramMaps.get('id');
        }),
        switchMap((paramsMap) => {
          this.id = paramsMap.get('id');
          return this.process$(this.getGalleryContent$(this.id));
        })
      )
      .pipe(
        catchError((error: IErrorResponse) => {
          if (error.status === 404) {
            this._dialogService.openErrorDialogAndNavigate(
              {
                title: 'Gallery not found',
                message:
                  'The gallery specified was not found. It may have been deleted.',
              },
              'dashboard'
            );
          } else {
            this._dialogService.openErrorDialogAndNavigate(
              {
                message:
                  'An error has occurred loading the gallery. Please contact IT.',
              },
              'dashboard'
            );
          }

          return of(null);
        })
      )
      .subscribe({
        next: (galleryContentDto: IGalleryContentDto) => {
          this.initialGalleryContent = galleryContentDto;
          this.form = this.buildForm();
          this.setEditMode(galleryContentDto.dynamicData.canEdit);
          this._initForm();
          this._findEditionLanguage(this.getCurrentWebsites());
        },
      });
  }

  private getGalleryContent$(id: string) {
    if (this._isVersionId(id)) {
      this.galleryContent$ = this.process$(
        this.galleryService.getByVersionId$(id)
      );
    } else {
      this.galleryContent$ = this.process$(this.galleryService.getById$(id));
    }

    return this.galleryContent$;
  }

  private setEditMode(newEditModeValue) {
    if (newEditModeValue !== this.isEditMode) {
      newEditModeValue
        ? this.redirectToEditContent(this.id)
        : this.redirectToReadOnlyContent(this.id);
      return;
    }
    this.isEditMode = newEditModeValue;

    this.isEditMode ? this.form.enable() : this.form.disable();
  }

  private redirectToReadOnlyContent(id: string) {
    this._router.navigate([
      '/content',
      ContentTypeRouterPaths[ContentTypes.GALLERY],
      id,
    ]);
  }

  private redirectToEditContent(id: string) {
    this._router.navigate([
      '/content',
      ContentTypeRouterPaths[ContentTypes.GALLERY],
      id,
      'edit',
    ]);
  }

  private _initForm(): void {
    this._initDeliveryTargets(this.getCurrentWebsites, this.getCurrentUrls);
    this._initHeadline(this.initialGalleryContent.headline);
    this._initStandfirst(this.initialGalleryContent.standfirst);
    this._initLanguages(this.getCurrentSelectedLanguage);
    this._initNextStates(
      this.initialGalleryContent.dynamicData.allowableNextStates
    );
    this._initPublishTime(
      this.initialGalleryContent.presetPublishDate,
      this.initialGalleryContent.timezoneIdentifier
    );

    this._initContentList(this.initialGalleryContent.contentLists);

    this._initAuthors(this.initialGalleryContent.authors);

    this._initColumnistContent(this.initialGalleryContent.isColumnistContent);

    this._initTagging(
      this.initialGalleryContent.tags,
      this.initialGalleryContent.topics,
      this.initialGalleryContent.relatedCompanies,
      this.initialGalleryContent.relatedPeople,
      this.topicService
    );

    this._initSurtitles(
      this.initialGalleryContent.surtitlesDetailed,
      this.initialGalleryContent.tags,
      this.initialGalleryContent.topics,
      this.initialGalleryContent.relatedCompanies,
      this.initialGalleryContent.relatedPeople,
      this.initialGalleryContent.relatedManagers,
      this.initialGalleryContent.relatedFunds,
      this.initialGalleryContent.relatedSectors,
      this.initialGalleryContent.relatedAssetClasses,
      this.initialGalleryContent.relatedEtfs,
      this.initialGalleryContent.relatedInvestmentTrusts,
      this.initialGalleryContent.relatedShares
    );

    this._initContentListTracking();

    this._initRelatedData(
      this.initialGalleryContent.relatedManagers,
      this.initialGalleryContent.relatedFunds,
      this.initialGalleryContent.relatedSectors,
      this.initialGalleryContent.relatedAssetClasses,
      this.initialGalleryContent.relatedEtfs,
      this.initialGalleryContent.relatedInvestmentTrusts,
      this.initialGalleryContent.relatedShares
    );
    this._initExternalLinks(this.initialGalleryContent.externalLinks);

    this._initContentVisibility(
      this.initialGalleryContent.restrictToRegisteredUsers,
      this.initialGalleryContent.notForGoogleNewsSitemaps,
      this.initialGalleryContent.notForNewsFeeds,
      this.initialGalleryContent.hideThisArticle,
      this.initialGalleryContent.paywallPremiumContent,
      this.initialGalleryContent.notForCompetitors
    );

    this._initRelatedArticles(this.initialGalleryContent.relatedContents);
    this._initMainVideoImage(
      this.initialGalleryContent.mainImage,
      null,
      this.initialGalleryContent.displayImageOnWebsite
    );

    this._initSlides(this.initialGalleryContent.slides);

    this._initFormValueChanges();
  }

  private _isVersionId(contentIdToCheck: string): boolean {
    return contentIdToCheck.includes('Gallery');
  }

  private _initFormValueChanges(): void {
    this._initLanguageValueChanges(this.getCurrentSelectedLanguage);
    this._initWebsitesValueChanges(this.getCurrentWebsites);
    this._initHeadlineValueChanges(this.getHeadline);
    this._initStandfirstValueChanges(this.getStandfirst);
    this._initNextStatesValueChanges(this.getNextStates);
    this._initPublishTimeValueChanges(
      this.getPresetPublishTime,
      this.getTimezone
    );
    this._initContentListValueChanges(this.getContentLists);

    this._initRelatedDataValueChanges(
      this.getRelatedManagers,
      this.getRelatedFunds,
      this.getRelatedSectors,
      this.getRelatedAssetClasses,
      this.getRelatedEtfs,
      this.getRelatedInvestmentTrusts,
      this.getRelatedShares
    );

    this._initRelatedArticlesValueChanges(this.getRelatedArticles);
    this._initExternalLinksValueChanges(this.getExternalLinks);
    this._initTaggingValueChanges(
      this.getTags,
      this.getTopics,
      this.getCompanies,
      this.getPeople
    );
    this._initSurtitlesValueChanges(this.getSurtitles);
    this._initContentVisibilityValueChanges(
      this.getRestrictToRegisteredUsers,
      this.getNotForGoogleNewsSitemaps,
      this.getNotForNewsFeeds,
      this.getHideThisArticle,
      this.getPaywallPremiumContent,
      this.getNotForCompetitors
    );

    this._initColumnistContentValueChanges(this.getIsColumnistContent);
    this._initSelectedAuthorsValueChanges(this.getSelectedAuthors);
    this._initMainImageVideoValueChanges(
      this.getMainImage,
      null,
      this.getDisplayImageOnWebsite
    );
    this._initSlidesValueChanges(this.getSlides);
  }

  private _updateForms(): void {
    this._updateDeliveryTargets(this.getCurrentWebsites, this.getCurrentUrls);
  }

  getCurrentSelectedLanguage = () => {
    return this.initialGalleryContent.languageCode;
  };

  getCurrentWebsites = (): string[] | null => {
    return this.initialGalleryContent.deliveryTargetCodes;
  };

  getCurrentUrls = (): IWebsiteUrl[] | null => {
    if (this.initialGalleryContent.status != ContentStatus.PUBLISHED) {
      return this.initialGalleryContent.dynamicData.previewUrls;
    }
    return this.initialGalleryContent.dynamicData.liveUrls;
  };

  getHeadline = (): string | null => {
    return this.initialGalleryContent.headline;
  };

  getStandfirst = (): string | null => {
    return this.initialGalleryContent.standfirst;
  };

  getNextStates = (): IAllowableState[] | null => {
    return this.initialGalleryContent.dynamicData.allowableNextStates;
  };

  getPresetPublishTime = (): Date | null => {
    return this.initialGalleryContent.presetPublishDate;
  };

  getTimezone = (): string | null => {
    return this.initialGalleryContent.timezoneIdentifier;
  };

  getContentLists = (): IContentListDto[] | null => {
    return this.initialGalleryContent.contentLists;
  };

  getRelatedManagers = (): IManager[] | null => {
    return this.initialGalleryContent.relatedManagers;
  };

  getRelatedFunds = (): IFund[] | null => {
    return this.initialGalleryContent.relatedFunds;
  };

  getRelatedSectors = (): ISector[] | null => {
    return this.initialGalleryContent.relatedSectors;
  };

  getRelatedAssetClasses = (): IAssetClass[] | null => {
    return this.initialGalleryContent.relatedAssetClasses;
  };

  getRelatedEtfs = (): IEtf[] | null => {
    return this.initialGalleryContent.relatedEtfs;
  };

  getRelatedInvestmentTrusts = (): IInvestmentTrust[] | null => {
    return this.initialGalleryContent.relatedInvestmentTrusts;
  };

  getRelatedShares = (): IShare[] | null => {
    return this.initialGalleryContent.relatedShares;
  };
  getExternalLinks = (): IExternalLink[] | null => {
    return this.initialGalleryContent.externalLinks;
  };

  getSurtitles = (): ISurtitle[] | null => {
    return this.initialGalleryContent.surtitlesDetailed;
  };

  getTags = (): ITag[] | null => {
    return this.initialGalleryContent.tags;
  };

  getTopics = (): ITopic[] | null => {
    return this.initialGalleryContent.topics;
  };

  getCompanies = (): ICompany[] | null => {
    return this.initialGalleryContent.relatedCompanies;
  };

  getPeople = (): IPerson[] | null => {
    return this.initialGalleryContent.relatedPeople;
  };
  getRestrictToRegisteredUsers = (): boolean | null => {
    return this.initialGalleryContent.restrictToRegisteredUsers;
  };
  getNotForGoogleNewsSitemaps = (): boolean | null => {
    return this.initialGalleryContent.notForGoogleNewsSitemaps;
  };
  getNotForNewsFeeds = (): boolean | null => {
    return this.initialGalleryContent.notForNewsFeeds;
  };
  getHideThisArticle = (): boolean | null => {
    return this.initialGalleryContent.hideThisArticle;
  };
  getPaywallPremiumContent = (): boolean | null => {
    return this.initialGalleryContent.paywallPremiumContent;
  };
  getNotForCompetitors = (): boolean | null => {
    return this.initialGalleryContent.notForCompetitors;
  };

  getRelatedArticles = (): IRelatedContent[] | null => {
    return this.initialGalleryContent.relatedContents;
  };

  getIsColumnistContent = (): boolean | null => {
    return this.initialGalleryContent.isColumnistContent;
  };

  getSelectedAuthors = (): IAuthor[] | null => {
    return this.initialGalleryContent.authors;
  };
  getMainImage = (): IImgMediaDto | null => {
    return this.initialGalleryContent.mainImage;
  };
  getDisplayImageOnWebsite = (): boolean => {
    return this.initialGalleryContent.displayImageOnWebsite;
  };
  getSlides = (): ISlide[] | null => {
    return this.initialGalleryContent.slides;
  };

  onCreateNewVersion(): void {
    this.isProcessingStateChange = true;

    this.sharedService
      .createNewVersionWithCurrentStateCheck$(
        this.createNewVersionFn$,
        this.deleteVersionFn$,
        this.initialGalleryContent.contentId,
        this.initialGalleryContent.id,
        this.initialGalleryContent.dynamicData.currentDraftVersion
      )
      .subscribe(() => {
        this._snackBar.open(`New gallery version created successfully`, null, {
          duration: 5 * 1000,
        });
        this.isProcessingStateChange = false;
        this.redirectToEditContent(this.initialGalleryContent.contentId);
      });
  }

  onUpdateState(stateAction: ContentStatus): void {
    this.isProcessingStateChange = true;
    const updateStateFn = (contentId: string, action: string) =>
      this.galleryService.updateState$(contentId, action);

    const setEditModeFn = (newEditModeValue: boolean) =>
      this.setEditMode(newEditModeValue);

    this.sharedService
      .updateState<IGalleryContentDto>(
        updateStateFn,
        this.getContentFn$,
        setEditModeFn,
        this.initialGalleryContent.id,
        stateAction,
        this.isEditMode
      )
      .pipe(
        tap((refreshedDto: IGalleryContentDto) => {
          this.initialGalleryContent = refreshedDto;
          this.form.markAsPristine();
        }),
        finalize(() => (this.isProcessingStateChange = false))
      )
      .subscribe(() => {});
  }

  onSave(): void {
    if (this.isNewGallery) {
      this.isSaving = true;
      const createFn$ = (content) => {
        return this.galleryService.create$(content);
      };
      this.sharedService
        .createNewContent$<IGalleryContentDto>(createFn$, this.buildGallery())
        .pipe(finalize(() => (this.isSaving = false)))
        .subscribe((x: ISaveContentResponseDto) => {
          this.form.markAsPristine();
          this._router.navigate([
            '/content',
            ContentTypeRouterPaths[ContentTypes.GALLERY],
            x.contentId,
            'edit',
          ]);
        });
    } else {
      const updateFn$ = (id: string, content: IGalleryContentDto) => {
        return this.galleryService.update$(id, content);
      };
      const staleRecordFn = (content: IGalleryContentDto) => {
        return this.handleStaleGallery(content);
      };

      this.isSaving = true;

      this.sharedService
        .saveContent<IGalleryContentDto>(
          updateFn$,
          this.getContentFn$,
          staleRecordFn,
          this.getCurrentWebsites(),
          this._buildDeliveryTargetCodes(),
          this.deliveryTargets$,
          this.buildGallery()
        )
        .pipe(finalize(() => (this.isSaving = false)))
        .subscribe((refreshedDto: IGalleryContentDto) => {
          this._snackBar.open('Gallery saved successfully', null, {
            duration: 5 * 1000,
          });
          this.initialGalleryContent = refreshedDto;
          this._updateForms();
          this.form.markAsPristine();
          this.signalRService.userSavedPage(
            this.currentUserName,
            refreshedDto.contentId
          );
        });
    }
  }

  private handleStaleGallery(newGalleryContent: IGalleryContentDto) {
    this._contentStateService.setContentDto(newGalleryContent);
    this._router.navigate([
      '/content',
      ContentTypeRouterPaths[ContentTypes.GALLERY],
      newGalleryContent.contentId,
      'conflict',
    ]);
  }

  deleteGallery() {
    const deleteFn$ = (contentId: string) => {
      return this.galleryService.delete$(contentId);
    };
    this.sharedService
      .deleteContent$(deleteFn$, this.initialGalleryContent.contentId)
      .subscribe({
        next: () => {
          this._router.navigate(['/dashboard']);
        },
      });
  }

  spikeGallery() {
    const spikeFn$ = (contentId: string) => {
      return this.galleryService.spike$(contentId);
    };

    this.sharedService
      .spikeContent$(spikeFn$, this.initialGalleryContent.contentId)
      .subscribe({
        next: () => {
          this._router.navigate(['/dashboard']);
        },
      });
  }

  repurposeGallery() {
    const repurposeFn$ = (versionId: string) => {
      return this.galleryService.repurpose$(versionId, ContentStatus.WRITING);
    };

    this.sharedService
      .repurposeContent$(repurposeFn$, this.initialGalleryContent.id)
      .subscribe({
        next: (result: IRepurposeContentResponse) => {
          this._router.navigate([
            '/content',
            ContentTypeRouterPaths[ContentTypes.GALLERY.toLowerCase()],
            result.newArticleContentId,
            'edit',
          ]);
        },
      });
  }

  onReactivateGallery(): void {
    this.isProcessingStateChange = true;

    this.sharedService
      .reactivateContent(
        this.getContentFn$,
        this.createNewVersionFn$,
        this.deleteVersionFn$,
        this.initialGalleryContent.contentId,
        this.initialGalleryContent.id,
        this.initialGalleryContent.dynamicData.latestPublishedVersionId,
        this.getCurrentWebsites(),
        this.initialGalleryContent.dynamicData.currentDraftVersion,
        this.deliveryTargets$,
        this.isPreviouslyPublishedVersion()
      )
      .pipe(finalize(() => (this.isProcessingStateChange = false)))
      .subscribe({
        next: () => {
          this._snackBar.open(`Gallery Reactivated successfully`, null, {
            duration: 5 * 1000,
          });
          this.redirectToEditContent(this.initialGalleryContent.contentId);
        },
      });
  }

  private isPreviouslyPublishedVersion(): boolean {
    return (
      this.initialGalleryContent.isPublished() &&
      this.initialGalleryContent?.dynamicData?.latestPublishedVersionId !==
        this.initialGalleryContent?.id
    );
  }

  deleteUnpublishedDraft() {
    this.sharedService
      .deleteUnpublishedDraft$(
        this.deleteVersionFn$,
        this.initialGalleryContent.contentId,
        this.initialGalleryContent.id
      )
      .subscribe({
        next: () => {
          this._router.navigate(['/dashboard']);
        },
      });
  }

  onEditionAdded(deliveryTargetCode: string): void {
    this._handleEditionAdded(deliveryTargetCode);
    this._findEditionLanguage(this._buildDeliveryTargetCodes());
  }

  onEditionRemoved(deliveryTargetCode: string): void {
    this._handleEditionRemoved(deliveryTargetCode);
    this._findEditionLanguage(this._buildDeliveryTargetCodes());
  }

  onAddToBody($event) {
    this.slideListComponent.onAddToBody($event);
  }

  onRemoveLink($event) {
    this.slideListComponent.onRemoveLink($event);
  }

  onTagItemSelected($event) {
    const typedTags = this.form.get(FORM_PATHS.TYPED_TAGS()) as FormArray;

    handleTagSelected($event, typedTags);
  }

  onTagItemRemoved($event: ITypedSelectListItem) {
    const typedTags = this.form.get(FORM_PATHS.TYPED_TAGS()) as FormArray;
    const surtitleControl: FormArray<FormControl<ISurtitle>> = this.form.get(
      FORM_PATHS.SURTITLES()
    ) as FormArray;

    handleTagRemoved($event, typedTags, surtitleControl);
  }

  onSurtitlePromoted($event: ITypedSelectListItem) {
    const surtitleControl: FormArray<FormControl<ISurtitle>> = this.form.get(
      FORM_PATHS.SURTITLES()
    ) as FormArray;

    handleTagAddedAsSurtitle($event, surtitleControl);
  }

  private buildGallery(): GalleryContentDto {
    const newGalleryContent = new GalleryContentDto({});
    Object.assign(newGalleryContent, this.initialGalleryContent);
    newGalleryContent.headline = this._buildHeadline();
    newGalleryContent.deliveryTargetCodes = this._buildDeliveryTargetCodes();
    newGalleryContent.languageCode = this._buildLanguage();
    newGalleryContent.standfirst = this._buildStandfirst();
    newGalleryContent.presetPublishDate = this._buildPresetPublishTime();
    newGalleryContent.timezoneIdentifier = this._buildTimezone();
    newGalleryContent.contentLists = this._buildContentList();
    newGalleryContent.relatedManagers = this._buildRelatedManagers();
    newGalleryContent.relatedFunds = this._buildRelatedFunds();
    newGalleryContent.relatedSectors = this._buildRelatedSectors();
    newGalleryContent.relatedAssetClasses = this._buildRelatedAssetClasses();
    newGalleryContent.relatedEtfs = this._buildRelatedEtfs();
    newGalleryContent.relatedInvestmentTrusts =
      this._buildRelatedInvestmentTrusts();
    newGalleryContent.relatedShares = this._buildRelatedShares();
    newGalleryContent.authors = this._buildSelectedAuthors();
    newGalleryContent.relatedContents = this._buildRelatedArticles();
    newGalleryContent.isColumnistContent = this._buildColumnistContent();
    newGalleryContent.externalLinks = this._buildExternalLinks();
    newGalleryContent.surtitlesDetailed = this._buildSurtitles();
    newGalleryContent.topics = this._buildTopics();
    newGalleryContent.tags = this._buildTags();
    newGalleryContent.relatedCompanies = this._buildCompanies();
    newGalleryContent.relatedPeople = this._buildPeople();
    newGalleryContent.restrictToRegisteredUsers =
      this._buildRestrictToRegisteredUsers();
    newGalleryContent.notForGoogleNewsSitemaps =
      this._buildNotForGoogleNewsSitemaps();
    newGalleryContent.notForNewsFeeds = this._buildNotForNewsFeeds();
    newGalleryContent.hideThisArticle = this._buildHideThisArticle();
    newGalleryContent.paywallPremiumContent =
      this._buildPaywallPremiumContent();
    newGalleryContent.notForCompetitors = this._buildNotForCompetitors();
    newGalleryContent.mainImage = this._buildMainImage();
    newGalleryContent.displayImageOnWebsite =
      this._buildDisplayImageOnWebsite();
    newGalleryContent.slides = this._buildSlides();
    newGalleryContent.maxSlideId = this._buildMaxSlideId();

    return newGalleryContent;
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
